import { useEffect, useRef, useState } from "react";
import IconButtonComponent from "../UI/buttons/IconButton";
import iconUtil from "../../utils/iconUtil";
import { ReactComponent as SettingsIcon } from "../../assets/icons/gear.svg";
import labels from "../../utils/labels";
import AccordionList from "../UI/AccordionList";
import HoverBoxAnchor from "../UI/hoverBox/HoverBoxAnchor";
import RulesetService, { Ruleset } from "../../service/rulesetService";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import useDataSelector from "../../hooks/useDataSelector";
import Modal from "../UI/Modal";
import Overlay from "../UI/Overlay";
import Header2 from "../UI/textFields/Header2";
import Body2 from "../UI/textFields/Body2";
import StandardButton from "../UI/buttons/StandardButton";

const RulesetSelector = () => {
  const dispatch = useAppDispatch();
  const { idScenario } = useDataSelector();
  const interceptorIsReady = useAppSelector(
    (state) => state.apiConfigurationState.interceptorIsReady,
  );
  const settingsIconRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [rulesets, setRulesets] = useState<Ruleset[]>([]);
  const [selectedRuleset, setSelectedRuleset] = useState<Ruleset | undefined>(
    undefined,
  );
  const [clickedRuleset, setClickedRuleset] = useState<Ruleset | undefined>(
    undefined,
  );

  useEffect(() => {
    if (settingsIconRef.current) {
      iconUtil.updateIconColor(
        settingsIconRef.current,
        "var(--col-interaction-tertiary)",
      );
    }
  }, []);

  const hasFetchedRulesets = useRef(false);

  useEffect(() => {
    if (!interceptorIsReady || hasFetchedRulesets.current) return;
    hasFetchedRulesets.current = true;
    RulesetService.getRulesets(dispatch)
      .then(setRulesets)
      .catch(() => {
        hasFetchedRulesets.current = false;
      });
  }, [interceptorIsReady, dispatch]);

  return (
    <div className="relative">
      <div onMouseDown={(e) => e.stopPropagation()}>
        <HoverBoxAnchor
          tooltipText={labels.selectRuleset}
          tooltip
          hide={isOpen}
        >
          <IconButtonComponent
            id="pondooSettings"
            icon={
              <span ref={settingsIconRef}>
                <SettingsIcon width={20} height={20} />
              </span>
            }
            onClick={() => setIsOpen((prev) => !prev)}
            ariaLabel={labels.ruleset}
          />
        </HoverBoxAnchor>
      </div>
      {isOpen && (
        <AccordionList
          width="240px"
          right="0"
          height="400px"
          label={labels.selectRuleset}
          options={rulesets}
          labelKey="name"
          selectedOption={selectedRuleset}
          onSelect={(ruleset) => {
            setClickedRuleset(ruleset);
            setIsModalOpen(true);
          }}
          onClickAway={() => setIsModalOpen(true)}
        />
      )}
      {isModalOpen && clickedRuleset && (
        <Overlay darkBackground clickAway={() => setIsModalOpen(false)}>
          <Modal>
            <Header2>{labels.runOptimization}</Header2>
            <Body2>
              {labels.wantToRunOptimizationWithRuleset} {clickedRuleset.name}
              ?{" "}
            </Body2>
            <div className="flex justify-end gap-[var(--spacing-sm)] w-full">
              <StandardButton
                variant="SECONDARY"
                disable={idScenario == null}
                onClick={async () => {
                  try {
                    await RulesetService.runOptimizationWithRuleset(
                      clickedRuleset.id,
                      idScenario!,
                      dispatch,
                    );
                    setSelectedRuleset(clickedRuleset);
                  } catch {
                    // error notification already dispatched by service
                  } finally {
                    setClickedRuleset(undefined);
                    setIsModalOpen(false);
                    setIsOpen(false);
                  }
                }}
                ariaLabel={labels.runOptimization}
              >
                {labels.runOptimization}
              </StandardButton>
              <StandardButton
                variant="PRIMARY"
                onClick={() => {
                  setIsModalOpen(false);
                  setIsOpen(false);
                }}
                ariaLabel={labels.cancel}
              >
                {labels.cancel}
              </StandardButton>
            </div>
          </Modal>
        </Overlay>
      )}
    </div>
  );
};

export default RulesetSelector;
